import React from "react";
import PropTypes from "prop-types";
import Collapse, { Panel } from "rc-collapse";
import { anyFieldTouched } from "app/utils/form-validator.js";
import BookingDurationList from "app/pages/PrePackage/components/BookingDurationList.jsx";
import BookingAccommodationList from "app/pages/PrePackage/rental/BookingAccommodationList.jsx";
import Popover from "app/pages/.shared/Popover.jsx";
import Loader from "app/pages/.shared/Loader.jsx";
import DurationLabel from "app/pages/.shared/DurationLabel";
import Period from "app/pages/.shared/Period.jsx";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { scrollToElement } from "app/utils/scroller";
import findIndex from "lodash/findIndex";
import { FormattedMessage, injectIntl } from "react-intl";
import { propTypes, untouch } from "redux-form";
import { RESOLUTION, STRATEGY } from "app/constants";
import Button from "app/pages/.shared/form/Button";
import {
	pushTagOnDepartureDateSelect,
	pushTagOnDurationSelect,
	pushTagOnRentalAccommodationSelect,
} from "app/utils/analytics";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import "../components/search-engine.scss";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import classNames from "classnames";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";
import AtolButtonLabel from "app/pages/Booking/AtolButtonLabel";
import Modal from "react-modal";
import AuthLayerMobile from "app/pages/Auth/AuthLayerMobile/AuthLayerMobile";
import AuthModal from "app/pages/Auth/AuthModal/AuthModal";
import { resolutionPropType } from "app/utils/propTypes";
import loadable from "@loadable/component";

const DatePicker = loadable(() =>
	import(/* webpackChunkName: "datepicker" */ "app/pages/PrePackage/components/DatePicker.jsx")
);

const ACCORDION = {
	DURATIONS_DATES_PANEL_KEY: "0",
	ACCOMMODATIONS_PANEL_KEY: "1",
	DEPARTURE_DATES_PANEL_KEY: "2",
};

const mobileModalStyle = {
	overlay: {
		zIndex: 6,
	},
	content: {
		top: "0",
		left: "0",
		right: "0",
		bottom: "0",
		border: "none",
		background: "none",
		overflow: "hidden",
	},
};

class RentalSearchEngine extends React.Component {
	constructor(props) {
		super(props);

		const selectedRentalAccommodation = props.booking.rentalAccommodation;
		const selectedDepartureDate = props.booking.departureDate;
		const selectedDuration = props.booking.duration;

		this.state = {
			// Par défaut, on veut fermer tous les volets du moteur de recherche si tous les options ont été sélectionnées
			// Sinon on ouvre le volet des durées par défaut
			accordionActiveKey:
				selectedRentalAccommodation && selectedDepartureDate && selectedDuration
					? -1
					: ACCORDION.DURATIONS_DATES_PANEL_KEY,
			authModalVisible: false,
		};
		this.changeAccordionActivePanel = this.changeAccordionActivePanel.bind(this);
		this.onRentalAccommodationSelection = this.onRentalAccommodationSelection.bind(this);
		this.onDurationSelection = this.onDurationSelection.bind(this);
		this.onPriceSelection = this.onPriceSelection.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
	}

	onDurationSelection(durationValue) {
		const duration = this.props.durations.find(duration => duration.value === durationValue);
		// On reset la selection de la chambre et de la date de depart quand on change de durée pour
		// éviter d'avoir un loader infini dans le calendrier de prix.
		// A voir plus tard pour ne reset que si la selection n'existe pas dans la liste des chambre
		this.props.updateBooking({ duration, rentalAccommodation: {}, departureDate: undefined });
		this.props.fields.rentalAccommodation.onChange("");
		this.props.fields.departureDate.onChange("");

		this.setState({
			accordionActiveKey: ACCORDION.ACCOMMODATIONS_PANEL_KEY,
		});

		const offset = this.props.match.path === "/booking/search" ? -28 : -75;
		scrollToElement(this.refs.duration, offset);

		pushTagOnDurationSelect(duration.value);
	}

	onRentalAccommodationSelection(rentalAccommodation) {
		this.props.updateBooking({
			rentalAccommodation,
			config: rentalAccommodation.config,
		});

		this.setState({
			accordionActiveKey: ACCORDION.DEPARTURE_DATES_PANEL_KEY,
		});

		const offset = this.props.match.path === "/booking/search" ? -28 : -75;
		scrollToElement(this.refs.duration, offset);

		pushTagOnRentalAccommodationSelect();
	}

	onPriceSelection(price) {
		const departureDate = price.dd;
		const endDate = price.ed;

		this.props.updateBooking({
			departureDate: departureDate,
			endDate: endDate,
			price: price.p,
			publicPrice: price.pp,
		});

		this.setState({
			accordionActiveKey: undefined,
		});

		const offset = this.props.match.path === "/booking/search" ? -28 : -75;
		scrollToElement(this.refs.duration, offset); // scroll pour afficher tout le moteur de recherche

		pushTagOnDepartureDateSelect(departureDate, endDate);
	}

	changeAccordionActivePanel(activeKey) {
		this.setState({
			accordionActiveKey: activeKey,
		});
	}

	componentDidUpdate(prevProps) {
		const { fields } = this.props;

		if (!isEqual(prevProps.fields, fields)) {
			const accommodationValue = get(fields, "rentalAccommodation.value");
			const durationValue = get(fields, "duration.value");
			const departureDateValue = get(fields, "departureDate.value");

			let defaultAccordionActiveKey;

			if (!durationValue) {
				defaultAccordionActiveKey = ACCORDION.DURATIONS_DATES_PANEL_KEY;
			} else if (!accommodationValue) {
				defaultAccordionActiveKey = ACCORDION.ACCOMMODATIONS_PANEL_KEY;
			} else if (!departureDateValue) {
				defaultAccordionActiveKey = ACCORDION.DEPARTURE_DATES_PANEL_KEY;
			} else {
				/* returning here to avoid the accordion to collapse when no criteria are met */
				return;
			}
			this.setState({ accordionActiveKey: defaultAccordionActiveKey });
		}
	}

	handleSubmit(...args) {
		const { handleSubmit, strategy, uuid } = this.props;
		if (strategy === STRATEGY.PRODUCT_FIRST && !uuid) {
			const [event] = args;
			// note: ici on fait un event.preventDefault() nous même afin d'éviter le rechargement de la page
			event.preventDefault();
			return this.setState({ authModalVisible: true });
		}
		return handleSubmit(...args);
	}

	render() {
		const {
			resolution,
			fields,
			prePackageOffer,
			submitting,
			allAvailableDurations,
			rentalAccommodations = [],
			durations = [],
			prices = [],
			months = [],
			booking,
			mealBasis,
			intl,
			error, // set to undefined on each redux-form/CHANGE
			shop,
			isDealExpired,
		} = this.props;

		const selectedDurationValue = get(booking, "duration.value");
		const selectedDepartureDateValue = get(booking, "departureDate");
		const selectedAccommodationValue = get(booking, "rentalAccommodation.accommodation");

		const [, countryCode] = shop.split("-");

		const bestPriceIndex = findIndex(
			this.props.prices,
			price => price.i && price.i.mp === true
		);
		const initialIndex = selectedDepartureDateValue
			? findIndex(this.props.prices, price => price.dd === selectedDepartureDateValue)
			: bestPriceIndex;

		const searchEngineDepartureDateNode =
			prices.length !== 0 ? (
				<div className="search-engine__price">
					<DatePicker
						isRental={true}
						initialIndex={initialIndex}
						months={months}
						prices={prices}
						field={fields.departureDate}
						onPriceChange={this.onPriceSelection}
					/>

					{mealBasis ? (
						<div className="search-engine__meal-basis">
							<div>
								<IconInfo width={15} height={15} style={{ marginRight: "5px" }} />
							</div>
							<FormattedMessage
								values={{
									meal: mealBasis,
								}}
								id="search.engine.meal.basis"
							/>
						</div>
					) : (
						false
					)}
				</div>
			) : (
				<div className="search-engine__loader">
					<Loader />
				</div>
			);

		const durationHeaderLabel =
			selectedDurationValue &&
			this.state.accordionActiveKey !== ACCORDION.DURATIONS_DATES_PANEL_KEY ? (
				<div className="search-engine__header" ref="duration">
					<div className="search-engine__label">
						<DurationLabel duration={selectedDurationValue} />
					</div>
					<div className="search-engine__edit">
						<FormattedMessage id="search.engine.edit.label" />
					</div>
				</div>
			) : (
				<div className="search-engine__header" ref="duration">
					<FormattedMessage id="search.engine.durations.title" />
				</div>
			);

		const rentalAccommodationHeaderLabel =
			selectedAccommodationValue &&
			this.state.accordionActiveKey !== ACCORDION.ACCOMMODATIONS_PANEL_KEY ? (
				<div className="search-engine__header" ref="rentalAccommodation">
					<div className="search-engine__label">{selectedAccommodationValue}</div>
					<div className="search-engine__edit">
						<FormattedMessage id="search.engine.edit.label" />
					</div>
				</div>
			) : (
				<div className="search-engine__header" ref="rentalAccommodation">
					<FormattedMessage id="search.engine.rental.accommodation.title" />
				</div>
			);

		const departureDateHeaderLabel =
			selectedDepartureDateValue &&
			this.state.accordionActiveKey !== ACCORDION.DEPARTURE_DATES_PANEL_KEY ? (
				<div className="search-engine__header" ref="departureDate">
					<div className="search-engine__label">
						{selectedDepartureDateValue ? (
							<Period
								prefix={<FormattedMessage id="general.period.prefix" />}
								suffix={<FormattedMessage id="general.period.suffix" />}
								fromDate={selectedDepartureDateValue}
								endDate={booking.endDate}
								format={{
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
								}}
							/>
						) : (
							false
						)}
					</div>
					<div className="search-engine__edit">
						<FormattedMessage id="search.engine.edit.label" />
					</div>
				</div>
			) : (
				<div className="search-engine__header" ref="departure-date">
					<FormattedMessage id="search.engine.departure.date.title" />
				</div>
			);

		const offerClassName = classNames("search-engine__offer", {
			"rc-collapse-item--disabled-filter": isDealExpired,
		});

		const rentalAccommodationClassName = classNames("cy-search-engine-accommodation-panel", {
			"rc-collapse-item-hidden": !selectedDurationValue,
		});

		const durationClassName = classNames("cy-search-engine-duration-panel", {
			"rc-collapse-item-disabled": isDealExpired,
			"rc-collapse-item-hidden": isDealExpired,
		});

		const departureDateAccommodationClassName = selectedAccommodationValue
			? ""
			: "rc-collapse-item-hidden";

		const footerClassName = classNames("search-engine__footer", {
			"search-engine__footer--hidden": !selectedDepartureDateValue,
		});

		const errorMessage = error ? intl.formatMessage(error) : "";

		const onMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

		return (
			<React.Fragment>
				<Modal isOpen={this.state.authModalVisible && onMobile} style={mobileModalStyle}>
					<AuthLayerMobile onAuthSuccess={this.closeAuthModal} />
				</Modal>
				<AuthModal
					isOpen={this.state.authModalVisible && !onMobile}
					onAuthSuccess={this.closeAuthModal}
				/>
				<form
					id="search-engine"
					className="search-engine search-engine--rental"
					onSubmit={this.handleSubmit}
				>
					<div className={offerClassName} ref="offer">
						<div className="rental-offer">
							<div className="rental-offer__header">
								<div className="rental-offer__type-label">
									<FormattedMessage id="search.engine.rental.offer.label" />:
								</div>
								<div className="rental-offer__type-value">
									<FormattedMessage id="search.engine.rental.without.transport.label" />
								</div>
							</div>

							{prePackageOffer.offers[0] ? (
								<div>
									<div className="rental-offer__price rental-offer__price--final">
										<AmountContainer
											prefix={
												<FormattedMessage id="offer.per.person.from.label" />
											}
											amount={prePackageOffer.offers[0].pricing.minPrice.p}
											suffix={
												<FormattedMessage id="general.amount.per.accommodation.suffix.label" />
											}
										/>
									</div>

									{prePackageOffer.offers[0].pricing.minPrice.pp ? (
										<div className="rental-offer__price rental-offer__price--public">
											<AmountContainer
												amount={
													prePackageOffer.offers[0].pricing.minPrice.pp
												}
												prefix={
													<FormattedMessage id="general.instead.of.price" />
												}
											/>
										</div>
									) : (
										false
									)}
								</div>
							) : (
								false
							)}

							{allAvailableDurations.length > 0 ? (
								<div className="rental-offer__available-durations">
									<AvailableDurations durations={allAvailableDurations} />
								</div>
							) : (
								false
							)}
						</div>
					</div>

					<Collapse
						accordion={true}
						onChange={this.changeAccordionActivePanel}
						activeKey={this.state.accordionActiveKey}
					>
						<Panel header={durationHeaderLabel} className={durationClassName}>
							<div className="search-engine__duration" ref="searchEngineDuration">
								<BookingDurationList
									amountSuffix={
										<FormattedMessage id="general.amount.per.accommodation.suffix.label.short" />
									}
									durations={durations}
									field={fields.duration}
									onDurationChange={this.onDurationSelection}
								/>
							</div>
						</Panel>

						<Panel
							header={rentalAccommodationHeaderLabel}
							className={rentalAccommodationClassName}
						>
							<div className="search-engine__rental-accommodation">
								<BookingAccommodationList
									rentalAccommodations={rentalAccommodations}
									field={fields.rentalAccommodation}
									onRentalAccommodationChange={
										this.onRentalAccommodationSelection
									}
								/>
							</div>
						</Panel>

						<Panel
							header={departureDateHeaderLabel}
							className={departureDateAccommodationClassName}
						>
							<div className="search-engine__departure-date">
								{searchEngineDepartureDateNode}
							</div>
						</Panel>
					</Collapse>

					<div className={footerClassName}>
						{this.props.booking.price ? (
							<div className="search-engine__selected-price">
								<AmountContainer
									amount={this.props.booking.price}
									suffix={
										<FormattedMessage id="general.amount.per.accommodation.suffix.label" />
									}
								/>
							</div>
						) : (
							false
						)}

						{this.props.booking.publicPrice ? (
							<div className="search-engine__selected-price search-engine__selected-price--public">
								<AmountContainer
									amount={this.props.booking.publicPrice}
									prefix={<FormattedMessage id="general.instead.of.price" />}
								/>
							</div>
						) : (
							false
						)}

						<div className="search-engine__action-button">
							<Button
								submit
								design="primary"
								loading={submitting}
								data-cy="search-engine-button"
							>
								<FormattedMessage id="search.engine.book.now" />
							</Button>
							<Popover
								onClose={() => {
									untouch(
										"booking-search-form",
										"offer",
										"departureCityCode",
										"departureDate",
										"duration",
										"adults",
										"children",
										"infants"
									);
								}}
								isOpen={anyFieldTouched(fields) && error}
							>
								{/* TODO passer a la version > 0.4.5 dereact-popover pour pouvoir utiliser un FormattedMessage au lieu de intl  */}
								{errorMessage}
							</Popover>
							<div className="search-engine__marketing-placeholder">
								{countryCode === "GB" && <AtolButtonLabel />}
							</div>
						</div>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

RentalSearchEngine.propTypes = {
	...propTypes,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	durations: PropTypes.array,
	prices: PropTypes.array,
	months: PropTypes.array,
	resolution: resolutionPropType,
	booking: PropTypes.object,
	updateBooking: PropTypes.func,
	mealBasis: PropTypes.string,
	intl: PropTypes.object,
	isDealExpired: PropTypes.bool,
};

export default injectIntl(RentalSearchEngine);
